import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Renderer2,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { DashboardConfig } from '../core/config/dashboard.config';
import { CSSClass, ViewSelector } from './dom.constant';
import { DashboardService } from '../dashboard.service';
import { Container } from '../core/dashboard.node';
import { Templates } from './templates.interface';
import { copy, Debounce } from '../core/utils';
import { DashboardState } from '../core/state';
import { JSON } from '../core/json.type';

declare interface ResizeObserver {
  observe(target: Element): void;
  unobserve(target: Element): void;
  disconnect(): void;
}

declare var ResizeObserver: {
  prototype: ResizeObserver;
  new (callback: ResizeObserverCallback): ResizeObserver;
};

interface ResizeObserverSize {
  inlineSize: number;
  blockSize: number;
}

type ResizeObserverCallback = (
  entries: ReadonlyArray<ResizeObserverEntry>,
  observer: ResizeObserver
) => void;

interface ResizeObserverEntry {
  readonly target: Element;
  readonly contentRect: DOMRectReadOnly;
  readonly borderBoxSize: ResizeObserverSize;
  readonly contentBoxSize: ResizeObserverSize;
}

@Component({
  selector: 'ak-dashboard-view',
  encapsulation: ViewEncapsulation.None,
  styleUrls: ['./view.component.scss'],
  template: `
    <div #wrapper class="{{ cssClass.DASHBOARD_WRAPPER }}">
      <ak-dashboard-container
        *ngIf="container"
        [node]="container"
        [service]="service"
        [templates]="templates"
      ></ak-dashboard-container>
    </div>
  `,
})
export class ViewComponent
  implements OnInit, OnChanges, AfterViewInit, OnDestroy
{
  @ViewChild(ViewSelector.WRAPPER, { static: false })
  private wrapper!: ElementRef;
  @Input() config!: DashboardConfig;
  @Input() templates!: Templates;
  @Input() nodes: any[] = [];

  public readonly cssClass: typeof CSSClass = CSSClass;
  public readonly service: DashboardService;
  private resizeObserver!: ResizeObserver;
  public containerGridVisible: boolean;
  public container!: Container;

  constructor(
    private change: ChangeDetectorRef,
    private rendered: Renderer2,
    private zone: NgZone
  ) {
    this.service = new DashboardService();
    this.containerGridVisible = false;
  }

  ngOnInit(): void {
    if (!this.config?.components) {
      throw new Error('Missing config components');
    } else if (this.config.allowDrag) {
      this.rendered.addClass(this.wrapper.nativeElement, CSSClass.EDIT_MODE);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const nodesChange = changes['nodes'];
    if (nodesChange) {
      this.config.state = this.getState(this.config.state);
      this.container = new Container({
        component: {} as any,
        config: this.config,
        parent: null,
        data: {
          name: 'Container',
          children: nodesChange.currentValue || [],
        },
      });

      this.change.detectChanges();
    }
  }

  ngAfterViewInit(): void {
    this.startWatchingDashboardRezise();
  }

  ngOnDestroy(): void {
    this.resizeObserver?.unobserve(this.wrapper.nativeElement);
  }

  @Debounce(100) // To fix google chrome random drag locked
  public showContainerBorderLine(): void {
    if (this.config.allowDrag && !this.containerGridVisible) {
      this.rendered.addClass(
        this.wrapper.nativeElement,
        CSSClass.CONTAINER_GRID
      );
      this.containerGridVisible = true;
    }
  }

  public hideContainerBorderLine(): void {
    if (this.config.allowDrag && this.containerGridVisible) {
      this.rendered.removeClass(
        this.wrapper.nativeElement,
        CSSClass.CONTAINER_GRID
      );
      this.containerGridVisible = false;
    }
  }

  public toJSON(): JSON<any> {
    return (<any>this.container.toJSON()).children;
  }

  public unSelectNode(): void {
    this.service.unsetSelectedNode();
  }

  private startWatchingDashboardRezise(): void {
    this.resizeObserver = new ResizeObserver((entries) => {
      this.zone.run(() => {
        this.service.dashboardWidth.next(entries[0].contentRect.width);
      });
    });

    this.resizeObserver.observe(this.wrapper.nativeElement);
  }

  private getState(state?: any): DashboardState {
    return copy<DashboardState>({
      activeNavigations: state?.activeNavigations || {},
      expandableNodes: state?.expandableNodes || {},
    });
  }
}
